import { ampli, type TokenRiskDialogViewedProperties } from '@/ampli';
import dynamic from 'next/dynamic';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import { useDedaubTokenCheck } from '@/hooks/useDedaubTokenCheck';

import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { LoadingSpinnerPrimary } from '@/ui/Icons/Animated/LoadingSpinner';
import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { RefreshArrowIcon } from '@/ui/Icons/RefreshArrowIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { routes } from '@/utils/routes';

const DeferredTokenSecurityWarningDialog = dynamic(() =>
  import('@/components/TransactionModule/TokenSecurityWarningDialog').then(
    ({ TokenSecurityWarningDialog }) => TokenSecurityWarningDialog,
  ),
);

interface Props {
  onClickApprove?: () => void;
  onSubmit: () => void;
  error?: Error | undefined;
  isLoading?: boolean;
  isQuoteExpired?: boolean;
  hasPriceImpact: boolean;
  needsApproval?: boolean;
  needsWalletConfirmation: boolean;
  onRefreshQuote?: () => void;
  sellToken?: TokenResult;
  buyToken?: TokenResult;
  sellTokenSymbol?: string;
  tokenApproval?: {
    isTokenApproved: boolean;
    didAnimateTokenApproval: boolean;
    setDidAnimateTokenApproval: (value: boolean) => void;
  };
}

export const PlaceOrderButton = ({
  hasPriceImpact,
  isLoading,
  isQuoteExpired,
  error,
  needsApproval,
  needsWalletConfirmation,
  onClickApprove,
  onSubmit,
  onRefreshQuote,
  sellToken,
  buyToken,
  sellTokenSymbol,
  tokenApproval,
}: Props) => {
  const [isRiskDialogOpen, setIsRiskDialogOpen] = useState(false);
  const riskDialogImpression = useRef<TokenRiskDialogViewedProperties | null>(null);

  const sellTokenCheck = useDedaubTokenCheck({
    address: sellToken?.address,
    chainId: sellToken?.chainId,
  });
  const buyTokenCheck = useDedaubTokenCheck({
    address: buyToken?.address,
    chainId: buyToken?.chainId,
  });

  const riskyTokenSide =
    sellTokenCheck.isFeatureEnabled && sellTokenCheck.data?.features.allowanceBypass === true
      ? 'sell'
      : buyTokenCheck.isFeatureEnabled && buyTokenCheck.data?.features.allowanceBypass === true
        ? 'buy'
        : undefined;
  const riskyToken =
    riskyTokenSide === 'sell' ? sellToken : riskyTokenSide === 'buy' ? buyToken : undefined;
  const hasTokenSecurityRisk = riskyToken !== undefined;

  const { isTokenApproved, didAnimateTokenApproval, setDidAnimateTokenApproval } =
    tokenApproval || {};
  const shouldShowApprovalAnimation = isTokenApproved && !didAnimateTokenApproval;
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);

  useEffect(() => {
    if (shouldShowApprovalAnimation) {
      // set animation complete to true after 2 seconds
      const approvedStepAnimationTimer = setTimeout(() => setDidAnimateTokenApproval?.(true), 2000);
      return () => clearTimeout(approvedStepAnimationTimer);
    }
  }, [setDidAnimateTokenApproval, shouldShowApprovalAnimation]);

  const handleSubmit = () => {
    if (!hasTokenSecurityRisk) {
      onSubmit();
      return;
    }

    const properties: TokenRiskDialogViewedProperties = {
      dialog_id: crypto.randomUUID(),
      side: riskyTokenSide as string,
      token_address: riskyToken.address,
      token_chain_id: riskyToken.chainId,
      token_symbol: riskyToken.symbol,
    };

    riskDialogImpression.current = properties;
    setIsRiskDialogOpen(true);
    ampli.tokenRiskDialogViewed(properties);
  };

  const handleRiskDialogDismissed = () => {
    setIsRiskDialogOpen(false);
    if (riskDialogImpression.current) {
      ampli.tokenRiskDialogDismissed(riskDialogImpression.current);
      riskDialogImpression.current = null;
    }
  };

  const handleRiskConfirmed = () => {
    if (isLoading || error || !hasTokenSecurityRisk) return;
    setIsRiskDialogOpen(false);
    if (riskDialogImpression.current) {
      ampli.tokenRiskDialogConfirmed(riskDialogImpression.current);
    }
    onSubmit();
  };

  if (needsWalletConfirmation) {
    return (
      <button className={buttons({ size: 'large', color: 'black', shape: 'pill' })} disabled>
        <LoadingSpinnerPrimary className="mr-3" />
        <span>Confirm in your wallet</span>
      </button>
    );
  } else if (isQuoteExpired) {
    return (
      <button
        className={buttons({
          size: 'large',
          color: 'gray',
          shape: 'pill',
          class: 'flex items-center justify-center gap-2',
        })}
        onClick={onRefreshQuote}
      >
        <RefreshArrowIcon /> Refresh quote
      </button>
    );
  }

  if (needsApproval || shouldShowApprovalAnimation) {
    return (
      <>
        <div className="flex flex-col items-center gap-4 w-full">
          {isTokenApproved ? (
            <div
              className={fontStyles({
                variant: 'LABEL_LG',
                class:
                  'bg-token-alphalight-200 border border-token-gray-300 rounded-2xl flex items-center justify-center h-13 max-w-full w-[404px]',
              })}
            >
              <CheckmarkIcon className="bg-token-green-300 text-token-green-1000 rounded-full h-6 w-6 mr-3 p-1 animate-pulse" />
              <span>Token approved</span>
            </div>
          ) : (
            <button
              data-testid="trade-approval-button"
              className={buttons({ size: 'large', color: 'black', shape: 'pill' })}
              disabled={isLoading}
              onClick={onClickApprove}
            >
              {`Approve spending${sellTokenSymbol ? ` ${sellTokenSymbol}` : ''}`}
            </button>
          )}
          <ExternalLink
            className={linkStyles({
              color: 'purple',
              font: 'LABEL_MD',
              underline: true,
            })}
            href={routes.HELP_APPROVE_TOKEN}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              window.open(routes.HELP_APPROVE_TOKEN, '_blank');
            }}
          >
            Why do I have to approve a token?
          </ExternalLink>
        </div>
      </>
    );
  }

  return (
    <>
      <button
        data-testid="trade-submit-button"
        className={buttons({
          color: !isLoading && (hasPriceImpact || hasTokenSecurityRisk) ? 'red' : 'black',
          size: 'large',
          shape: 'pill',
        })}
        disabled={isLoading || !!error}
        onClick={handleSubmit}
      >
        {isLoading && <LoadingSpinnerPrimary className="mr-3" />}
        {!isLoading && hasTokenSecurityRisk
          ? 'Review risk'
          : recipientAddress
            ? 'Place order & Transfer'
            : 'Place order'}
      </button>
      <DeferredTokenSecurityWarningDialog
        isOpen={isRiskDialogOpen && hasTokenSecurityRisk && !isLoading && !error}
        onClose={handleRiskDialogDismissed}
        onConfirm={handleRiskConfirmed}
        tokenSymbol={riskyToken?.symbol}
      />
    </>
  );
};
